import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

import HoverObserver from "./hover-observer";
import HoverAnimator from "./hover-animator";
import LinkObject from "./link-object";

export default class SiteMap extends HTMLElement {

    config = {
        scene: "objects/map.json",
        activeColor: '0xff00ff',
        currentModel: '',
        hoverAnimations: {},
        hoverLinks: {},
    };

    constructor(config = {}) {
        super();
        
        this.config.scene = config.scene ?? this.config.scene;
        this.config.activeColor = config.activeColor ?? this.config.activeColor;
        this.config.currentModel = config.currentModel ?? this.config.currentModel;
        this.config.hoverAnimations = config.hoverAnimations ?? this.config.hoverAnimations;
        this.config.hoverLinks = config.hoverLinks ?? this.config.hoverLinks;
        
        this._frame = this._frame.bind(this);

        this._scene = new THREE.Scene();

        this._camera = new THREE.PerspectiveCamera();
        this._repositionCamera();

        this._renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
        this._renderer.shadowMap.enabled = true;
        this._renderer.shadowMap.type = THREE.PCFShadowMap;
        this._renderer.outputColorSpace = THREE.SRGBColorSpace;
        this._renderer.physicallyCorrectLights = true;

        this._controls = new OrbitControls(this._camera, this._renderer.domElement);
        this._controls.minDistance = 10;
        this._controls.maxDistance = 60;
        this._controls.maxPolarAngle = 0.5 * Math.PI;

        this._running = false;
        this._paused = false;
    }

    async connectedCallback() {
        await this.loadScene(this.config.scene);
        await this.start();

        this.setActiveModel(this.config.currentModel, this.config.activeColor);
        this.setHoverAnimations(this.config.hoverAnimations);
        this.setHoverLinks(this.config.hoverLinks);
    }

    async loadScene(url) {
        const loader = new THREE.ObjectLoader();
        const scene = await loader.loadAsync(url);
        this._scene.add(scene);

        this._controls.target = scene.position;
    }

    async start() {
        this._updateSize();
        this._observeResize();
        this.appendChild(this._renderer.domElement);

        this._running = true;
        requestAnimationFrame(this._frame);
    }

    setHoverAnimations(config) {
        this._animationHoverObserver?.unobserve();

        this._animationHoverObserver = new HoverObserver();
        this._animationHoverObserver.observe(this);

        this._hoverAnimator = new HoverAnimator(this._scene);

        for (const [objectName, animationConfig] of Object.entries(config)) {
            const obj = this._scene.getObjectByName(objectName);
            if (!obj) {
                continue;
            }

            if (!animationConfig)
                continue;

            switch (animationConfig.type) {
                case 'custom': this.registerCustomAnimations(obj, animationConfig); break;
                case 'color': this.registerColorAnimations(objectName, obj, animationConfig); break;
            }

            obj.animations.forEach((animation) => {
                this._hoverAnimator.addObject(obj, animation);
            });
        }

        this._hoverAnimator.observe(this._animationHoverObserver);
    }

    registerCustomAnimations(object, config) {
        if (!config.identifier)
            return;

        const animation = this._getAnimationByName(config.identifier);
        if (!animation)
            return;

        this._hoverAnimator.addObject(object, animation);
    }

    registerColorAnimations(objectName, object, config) {
        const startColor = config.startColor ? new THREE.Color(config.startColor) : object.material.color ?? new THREE.Color('#ffffff');
        const endColor = new THREE.Color(config.endColor ?? this.config.activeColor ?? '#000000');

        const duration = config.duration ?? .4;

        const animation = {
            uuid: crypto.randomUUID(),
            name: objectName + '.hover-color',
            duration: duration,
            tracks: [
                {
                    name: objectName + '.material.color',
                    times: [0, duration],
                    values: [startColor.r, startColor.g, startColor.b, endColor.r, endColor.g, endColor.b],
                    type: 'vector'
                },
                {
                    name: this.config.currentModel + '.material.color',
                    times: [0, duration],
                    values: [endColor.r, endColor.g, endColor.b, startColor.r, startColor.g, startColor.b],
                    type: 'vector'
                }
            ]
        };

        this._hoverAnimator.addObject(object, THREE.AnimationClip.parse(animation));
    }

    setHoverLinks(config) {
        this._linkHoverObserver?.unobserve();

        this._linkHoverObserver = new HoverObserver();
        this._linkHoverObserver.observe(this);

        for (const [name, href] of Object.entries(config)) {
            const object = this._scene.getObjectByName(name);
            if (!object || !href) {
                continue;
            }

            const linkObject = new LinkObject(object, href);
            linkObject.observe(this._linkHoverObserver);
        }
    }

    get dimensions() {
        const { width, height } = this.getBoundingClientRect();
        return { width, height };
    }

    set paused(paused) {
        if (paused === true) {
            this._paused = true;
        } else {
            this._paused = false;
            if (!this._running) {
                this._running = true;
                requestAnimationFrame(this._frame);
            }
        }
    }

    _updateSize() {
        const { width, height } = this.dimensions;

        this._camera.aspect = width / height;
        this._camera.updateProjectionMatrix();

        this._renderer.setSize(width, height);
    }

    _observeResize() {
        const ro = new ResizeObserver(entries => {
            entries.forEach(() => this._updateSize());
        });

        ro.observe(this);
        this._resizeObserver = ro;
    }

    _repositionCamera() {
        this._camera.position.set(0, 12, 23);
        this._camera.lookAt(0,0,0);
    }

    _frame() {
        if (this._running) {
            if (this._paused) {
                this._running = false;
                return;
            }
            requestAnimationFrame(this._frame);
        }

        this._controls.update();

        this._linkHoverObserver?.update(this._camera);
        this._animationHoverObserver?.update(this._camera);

        this._hoverAnimator?.update();

        this._renderer.render(this._scene, this._camera);
    }

    _getAnimationByName(name) {
        function find(node) {
            const a = node.animations.find(a => a.name === name);
            if (a) {
                return a;
            }

            for (const child of node.children) {
                const a = find(child);
                if (a) {
                    return a;
                }
            }
        }

        return find(this._scene);
    }

    setActiveModel(currentModel, activeColor) {
        let model = this._scene.getObjectByName(currentModel);
        if (model == null)
            return;

        model.material.color = new THREE.Color(activeColor);
    }

    toggleGroup(groupName) {
        const group = this._scene.getObjectByName(groupName);
        if (group == null)
            return;

        group.visible = !group.visible;
    }

    show() {
        if (!this.hidden)
            return;
        
        this.hidden = false;
        this.paused = false;
        document.body.setAttribute('data-site-map', 1);
    }

    hide() {
        if (this.hidden)
            return;

        this.hidden = true;
        this.paused = true;
        document.body.setAttribute('data-site-map', 0);
    }
}
